import { useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import useABTest from './useABTest';
import { setAgentHandoff, getAgentHandoff, setDestinationVariant } from '../utils/aiAgentHandoff';
import { isPreviewExperimentOverrideHost } from '../utils/experimentOverrideHosts';

/**
 * useDestinationVariant - A/B Test Hook for Sales Flow Destination
 *
 * This hook determines whether a user should go to checkout (must pay) or
 * directly to AI Agent (gets free trial).
 *
 * SECURITY: This hook is critical for the payment flow. The destinationVariant
 * must be saved to the handoff so AI Agent knows whether to grant free access.
 *
 * PostHog Flag: sales_prompt_destination
 * Variants:
 *   - 'checkout' (control): User goes to checkout page, must pay
 *   - 'ai_agent': User goes directly to AI Agent, gets free trial
 *
 * @returns {Object} { destinationVariant, isLoading, updateHandoffWithDestination, getDestinationPath }
 */
const useDestinationVariant = () => {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // A/B test for destination: checkout vs ai_agent
  const { variant: destinationFlagVariant, isLoading } = useABTest(
    'sales_prompt_destination_test',
    'sales_prompt_destination',
    'checkout', // default to checkout (control) if flag not loaded - FAIL CLOSED
    {
      source: location.pathname,
    }
  );

  // Allow query param override for testing: ?dv=checkout|ai_agent|control
  const destinationOverride = isPreviewExperimentOverrideHost() ? searchParams.get('dv') : null;
  const VALID_DESTINATION_VARIANTS = ['checkout', 'ai_agent', 'control'];

  const rawDestinationVariant = useMemo(() => {
    if (destinationOverride && VALID_DESTINATION_VARIANTS.includes(destinationOverride)) {
      return destinationOverride;
    }
    return destinationFlagVariant;
  }, [destinationOverride, destinationFlagVariant]);

  // Map 'control' to 'checkout' (PostHog uses 'control' as the control variant name)
  // SECURITY: FAIL CLOSED - default to 'checkout' for any null/undefined/invalid values
  const destinationVariant = useMemo(() => {
    // Handle null, undefined, or 'control' -> map to checkout (must pay)
    if (!rawDestinationVariant || rawDestinationVariant === 'control') {
      return 'checkout';
    }
    // Only allow known valid variants
    if (rawDestinationVariant === 'ai_agent' || rawDestinationVariant === 'checkout') {
      return rawDestinationVariant;
    }
    // Unknown variant -> fail closed to checkout
    return 'checkout';
  }, [rawDestinationVariant]);

  // Destination paths
  const DESTINATION_PATHS = {
    checkout: '/checkout/starter-plan',
    ai_agent: '/ai-agent',
  };

  /**
   * Updates the handoff payload with the destination variant.
   * CRITICAL: Call this before navigating to ensure AI Agent knows the user's variant.
   * Also sets the destination variant in separate storage so it survives handoff consumption.
   */
  const updateHandoffWithDestination = useCallback(() => {
    const handoff = getAgentHandoff() || {};
    const updatedHandoff = {
      ...handoff,
      destinationVariant,
    };
    setAgentHandoff(updatedHandoff);
    // Also set in separate storage so it survives handoff consumption
    // This prevents checkout users from bypassing payment by hitting the back button
    setDestinationVariant(destinationVariant);
    console.log('[useDestinationVariant] Updated handoff with destinationVariant:', destinationVariant);
    return updatedHandoff;
  }, [destinationVariant]);

  /**
   * Gets the destination path based on the A/B test variant.
   * @param {URLSearchParams} additionalParams - Additional query params to add to the URL
   * @returns {string} The full destination URL
   */
  const getDestinationPath = useCallback((additionalParams = new URLSearchParams()) => {
    const destinationPath = DESTINATION_PATHS[destinationVariant] || DESTINATION_PATHS.checkout;

    // For checkout destination, add redirect=starter_plan so users go to AI Agent after payment
    if (destinationPath.includes('/checkout/')) {
      additionalParams.set('redirect', 'starter_plan');
    }

    const queryString = additionalParams.toString();
    return queryString ? `${destinationPath}?${queryString}` : destinationPath;
  }, [destinationVariant]);

  return {
    destinationVariant,
    isLoading,
    updateHandoffWithDestination,
    getDestinationPath,
    // Helper properties
    isCheckoutVariant: destinationVariant === 'checkout',
    isAiAgentVariant: destinationVariant === 'ai_agent',
  };
};

export default useDestinationVariant;
